import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, LoaderCircle, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { canManageFactory } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { type CampoFiltro, FiltrosBar } from "@/components/ui/filtros-bar";
import { ImportButton } from "@/components/ui/ImportButton";
import { Modal } from "@/components/ui/modal";
import { useMe } from "@/lib/auth";
import { useEquipos } from "@/lib/equipos";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_PROCEDIMIENTOS } from "@/lib/filtros/procedimientos";
import {
  type Procedimiento,
  useAsignacionMasiva,
  useCreateProcedimiento,
  useDeleteProcedimiento,
  useProcedimientos,
  useUpdateProcedimiento,
} from "@/lib/procedimientos";
import { AsignacionMasivaForm, type AsignacionMasivaFormValues } from "./AsignacionMasivaForm";
import { ProcedimientoForm, type ProcedimientoFormValues } from "./ProcedimientoForm";

// toInput normaliza los valores del formulario al payload del API (pasos recortados sin
// vacíos, verificación vacía ⇒ null).
function toInput(values: ProcedimientoFormValues) {
  const energia = values.verificacion_energia_cero.trim();
  return {
    id_equipo: values.id_equipo,
    codigo_procedimiento: values.codigo_procedimiento.trim(),
    version_procedimiento: values.version_procedimiento.trim(),
    pasos: values.pasos.map((p) => p.text.trim()).filter((p) => p !== ""),
    verificacion_energia_cero: energia === "" ? null : energia,
    activo: values.activo,
  };
}

// toAsignacion normaliza el formulario de asignación masiva: con "todos loteables" no se
// envían ids concretos (el backend resuelve el conjunto).
function toAsignacion(values: AsignacionMasivaFormValues) {
  const energia = values.verificacion_energia_cero.trim();
  return {
    codigo_procedimiento: values.codigo_procedimiento.trim(),
    version_procedimiento: values.version_procedimiento.trim(),
    pasos: values.pasos.map((p) => p.text.trim()).filter((p) => p !== ""),
    verificacion_energia_cero: energia === "" ? null : energia,
    activo: values.activo,
    id_equipos: values.todos_loteables ? [] : values.id_equipos,
    todos_loteables: values.todos_loteables,
  };
}

/**
 * ProcedimientosPage lista los procedimientos LOTO con su equipo, código y versión, y
 * ofrece el CRUD completo (alta/edición con editor de pasos, borrado con confirmación)
 * más la ASIGNACIÓN MASIVA (clonar un procedimiento a varios equipos o a todos los
 * loteables). La tabla usa el DataTable de react-table (#34) SIN buscador en memoria
 * (`sinBuscador`): el texto libre y los filtros por equipo y estado son del SERVIDOR y viven
 * en la FiltrosBar (#247), y la tabla solo ordena y pagina, así que sus `accessorFn`
 * alimentan la celda, la ordenación o ambas, nunca una búsqueda (#346). (Procedimientos aún
 * no tiene endpoint de import.)
 */
export function ProcedimientosPage() {
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeGestionar = me != null && canManageFactory(me, obraId);

  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_PROCEDIMIENTOS);
  const {
    data: procedimientos,
    base: procedimientosBase,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = useProcedimientos(obraId, filtros);
  const filas = procedimientos ?? [];
  const { data: equipos } = useEquipos(obraId);
  const crear = useCreateProcedimiento(obraId);
  const editar = useUpdateProcedimiento(obraId);
  const borrar = useDeleteProcedimiento(obraId);
  const asignar = useAsignacionMasiva(obraId);

  const [formState, setFormState] = useState<
    { mode: "nueva" } | { mode: "editar"; procedimiento: Procedimiento } | null
  >(null);
  const [aBorrar, setABorrar] = useState<Procedimiento | null>(null);
  const [masivaOpen, setMasivaOpen] = useState(false);
  const [resumenMasiva, setResumenMasiva] = useState<number | null>(null);

  const listaEquipos = equipos ?? [];
  const sinEquipos = listaEquipos.length === 0;

  const columns = useMemo<ColumnDef<Procedimiento, unknown>[]>(() => {
    const cols: ColumnDef<Procedimiento, unknown>[] = [
      {
        accessorKey: "codigo_procedimiento",
        header: "Código",
        cell: ({ row }) => (
          <span className="font-medium text-slate-900">{row.original.codigo_procedimiento}</span>
        ),
      },
      {
        accessorKey: "version_procedimiento",
        header: "Versión",
        cell: ({ row }) => (
          <span className="text-slate-500 tabular-nums">{row.original.version_procedimiento}</span>
        ),
      },
      {
        id: "equipo",
        accessorFn: (p) => `${p.codigo_equipo} · ${p.nombre_equipo}`,
        header: "Equipo",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden sm:table-cell" },
      },
      {
        id: "pasos",
        accessorFn: (p) => p.pasos.length,
        header: "Pasos",
        enableGlobalFilter: false,
        cell: ({ row }) => (
          <span className="text-slate-500 tabular-nums">{row.original.pasos.length}</span>
        ),
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "activo",
        accessorFn: (p) => (p.activo ? "Activo" : "Inactivo"),
        header: "Estado",
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 font-medium text-sm ${
              row.original.activo ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"
            }`}
          >
            {row.original.activo ? "Activo" : "Inactivo"}
          </span>
        ),
      },
    ];
    if (puedeGestionar) {
      cols.push({
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Editar ${row.original.codigo_procedimiento}`}
              onClick={() => setFormState({ mode: "editar", procedimiento: row.original })}
            >
              <Pencil className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Eliminar ${row.original.codigo_procedimiento}`}
              onClick={() => setABorrar(row.original)}
            >
              <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            </Button>
          </div>
        ),
      });
    }
    return cols;
  }, [puedeGestionar]);

  // Campos de la barra de filtros. Los valores son los del API (ids y la revisión
  // documental tal cual), no las etiquetas; los equipos salen del listado relacionado.
  // Las versiones se ofrecen a partir de las presentes en la lista base.
  const campos = useMemo<CampoFiltro[]>(
    () => [
      { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar procedimientos…" },
      {
        tipo: "select",
        nombre: "equipo",
        etiqueta: "Equipo",
        opciones: listaEquipos.map((e) => ({
          valor: String(e.id),
          etiqueta: `${e.codigo_equipo} · ${e.nombre_equipo}`,
        })),
      },
      {
        tipo: "select",
        nombre: "version",
        etiqueta: "Versión",
        opciones: Array.from(
          new Set((procedimientosBase ?? []).map((p) => p.version_procedimiento)),
        )
          .sort((a, b) => a.localeCompare(b))
          .map((v) => ({ valor: v, etiqueta: v })),
      },
      { tipo: "booleano", nombre: "activo", etiqueta: "Activo" },
    ],
    [listaEquipos, procedimientosBase],
  );

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: ProcedimientoFormValues) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({ id: formState.procedimiento.id, input: toInput(values) });
    } else {
      await crear.mutateAsync(toInput(values));
    }
    cerrarForm();
  }

  async function onSubmitMasiva(values: AsignacionMasivaFormValues) {
    const res = await asignar.mutateAsync(toAsignacion(values));
    setMasivaOpen(false);
    setResumenMasiva(res.creados);
  }

  async function confirmarBorrado() {
    if (!aBorrar) {
      return;
    }
    try {
      await borrar.mutateAsync(aBorrar.id);
      setABorrar(null);
    } catch {
      // El fallo se refleja en borrar.error y se muestra en el modal.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Procedimientos</h1>
          <p className="text-base text-slate-500">Procedimientos LOTO versionados por equipo.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <ImportButton tipo="procedimientos" etiqueta="procedimientos" obraId={obraId} />
          {/* La exportación arrastra los filtros de la URL (#249 D9). */}
          <ExportButton tipo="procedimientos" obraId={obraId} filtros={filtros} />
          {puedeGestionar ? (
            <>
              <Button variant="outline" onClick={() => setMasivaOpen(true)} disabled={sinEquipos}>
                <Users className="h-5 w-5" aria-hidden="true" />
                <span className="hidden sm:inline">Asignación masiva</span>
              </Button>
              <Button onClick={() => setFormState({ mode: "nueva" })} disabled={sinEquipos}>
                <Plus className="h-5 w-5" aria-hidden="true" />
                Nuevo
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {puedeGestionar && sinEquipos ? (
        <Alert variant="info">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Crea primero un equipo para poder dar de alta procedimientos.</span>
        </Alert>
      ) : null}

      {resumenMasiva !== null ? (
        <Alert variant="info">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            Asignación masiva completada: se crearon <strong>{resumenMasiva}</strong>{" "}
            {resumenMasiva === 1 ? "procedimiento" : "procedimientos"}.
          </span>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar los procedimientos. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando procedimientos…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay procedimientos.
          {puedeGestionar && !sinEquipos ? " Crea el primero con «Nuevo»." : ""}
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={campos}
            valor={filtros}
            onChange={setFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={filas}
              sinBuscador
              onRowClick={
                puedeGestionar
                  ? (procedimiento) => setFormState({ mode: "editar", procedimiento })
                  : undefined
              }
            />
          ) : null}
        </>
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar procedimiento" : "Nuevo procedimiento"}
      >
        {formState !== null ? (
          <ProcedimientoForm
            equipos={listaEquipos}
            defaultValues={
              formState.mode === "editar"
                ? {
                    id_equipo: formState.procedimiento.id_equipo,
                    codigo_procedimiento: formState.procedimiento.codigo_procedimiento,
                    version_procedimiento: formState.procedimiento.version_procedimiento,
                    pasos:
                      formState.procedimiento.pasos.length > 0
                        ? formState.procedimiento.pasos.map((text) => ({ text }))
                        : [{ text: "" }],
                    verificacion_energia_cero:
                      formState.procedimiento.verificacion_energia_cero ?? "",
                    activo: formState.procedimiento.activo,
                  }
                : {
                    id_equipo: listaEquipos[0]?.id ?? 0,
                    codigo_procedimiento: "",
                    version_procedimiento: "1",
                    pasos: [{ text: "" }],
                    verificacion_energia_cero: "",
                    activo: true,
                  }
            }
            submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear procedimiento"}
            pending={crear.isPending || editar.isPending}
            onSubmit={onSubmitForm}
            onCancel={cerrarForm}
          />
        ) : null}
      </Modal>

      {/* Asignación masiva */}
      <Modal
        open={masivaOpen}
        onClose={() => setMasivaOpen(false)}
        title="Asignación masiva"
        className="max-w-lg"
      >
        {masivaOpen ? (
          <AsignacionMasivaForm
            equipos={listaEquipos}
            pending={asignar.isPending}
            onSubmit={onSubmitMasiva}
            onCancel={() => setMasivaOpen(false)}
          />
        ) : null}
      </Modal>

      {/* Confirmación de borrado */}
      <Modal
        open={aBorrar !== null}
        onClose={() => setABorrar(null)}
        title="Eliminar procedimiento"
      >
        <p className="text-base text-slate-600">
          ¿Seguro que quieres eliminar el procedimiento{" "}
          <strong>{aBorrar?.codigo_procedimiento}</strong> (versión {aBorrar?.version_procedimiento}
          )? Esta acción lo da de baja.
        </p>
        {borrar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudo eliminar el procedimiento. Inténtalo de nuevo.</span>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setABorrar(null)} disabled={borrar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarBorrado()}
            disabled={borrar.isPending}
          >
            {borrar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Eliminar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
